import jsPDF from "jspdf";
import { CATEGORIES, SEVERITY_LABELS } from "./diseases";
import { MEDICATIONS } from "./medications";

const COMPANY = {
  name: "AnagaHealth",
  brand: "AnagaHealth / CHEM",
  address: "803/P, Padmanagar phase-II, Quthbullapur, Hyderabad-500067",
  web: "www.anagahealth.com",
};

const CAT_RGB = {
  pneumoconiosis:   [255, 138, 0],
  skin:             [251, 191, 36],
  eye:              [34, 211, 238],
  ent:              [56, 189, 248],
  chemical:         [255, 0, 60],
  physical:         [251, 146, 60],
  radiation:        [255, 215, 0],
  infectious:       [163, 230, 53],
  tumors:           [39, 39, 42],
  musculoskeletal:  [52, 211, 153],
  mental:           [129, 140, 248],
  other:            [212, 212, 216],
};

const SEV_RGB = {
  extreme: [255, 0, 60],
  high:    [255, 138, 0],
  warning: [255, 215, 0],
  safe:    [0, 229, 255],
};

const isDarkCat = (id) => id === "tumors" || id === "mental";

/** Draw a single disease "fact sheet" page onto the supplied pdf. */
function drawDiseasePage(pdf, disease, pageNumber) {
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const M = 48;
  const meds = MEDICATIONS[disease.id] || [];
  const cat = CATEGORIES.find((c) => c.id === disease.cat) || {};

  // Header strip
  pdf.setFillColor(9, 9, 11);
  pdf.rect(0, 0, W, 110, "F");

  // Category swatch with code
  const catColor = CAT_RGB[disease.cat] || [200, 200, 200];
  pdf.setFillColor(...catColor);
  pdf.rect(M, 22, 70, 70, "F");
  pdf.setDrawColor(255, 255, 255);
  pdf.setLineWidth(2);
  pdf.rect(M, 22, 70, 70, "S");
  const darkInner = isDarkCat(disease.cat);
  pdf.setTextColor(darkInner ? 255 : 9, darkInner ? 255 : 9, darkInner ? 255 : 11);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(26);
  pdf.text(disease.code, M + 35, 64, { align: "center" });
  pdf.setFontSize(7);
  pdf.text(`CAT ${cat.n || "--"}`, M + 35, 84, { align: "center" });

  // Title block
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(20);
  pdf.text(disease.name.toUpperCase(), M + 90, 50);

  if (disease.aka) {
    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(9);
    pdf.setTextColor(180, 180, 180);
    pdf.text(disease.aka, M + 90, 64);
  }

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.setTextColor(180, 180, 180);
  pdf.text(`${cat.title || ""}  ·  ${COMPANY.brand}`, M + 90, 80);

  // Severity pill
  const sev = SEV_RGB[disease.severity] || [200, 200, 200];
  pdf.setFillColor(...sev);
  pdf.rect(W - M - 110, 32, 110, 32, "F");
  pdf.setTextColor(disease.severity === "extreme" ? 255 : 0,
                   disease.severity === "extreme" ? 255 : 0,
                   disease.severity === "extreme" ? 255 : 0);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(8);
  pdf.text("SEVERITY", W - M - 55, 44, { align: "center" });
  pdf.setFontSize(13);
  pdf.text(SEVERITY_LABELS[disease.severity] || "—", W - M - 55, 58, { align: "center" });

  // Sections
  let y = 130;

  const section = (label, body, color, dark = false) => {
    if (!body) return;
    // Check page break
    if (y > H - 150) {
      drawFooter(pdf, pageNumber, true);
      pdf.addPage();
      y = M;
    }
    pdf.setFillColor(...color);
    pdf.rect(M, y, W - 2 * M, 22, "F");
    pdf.setTextColor(dark ? 255 : 0, dark ? 255 : 0, dark ? 255 : 0);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.text(label, M + 10, y + 15);
    y += 28;
    pdf.setTextColor(9, 9, 11);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    const lines = pdf.splitTextToSize(body, W - 2 * M - 10);
    pdf.text(lines, M + 5, y);
    y += lines.length * 12 + 10;
  };

  section("CAUSE", disease.cause, [255, 215, 0]);
  section("WHAT IT IS", disease.what, [244, 244, 245]);
  if (disease.forms) section("FORMS / STAGES", disease.forms, [228, 228, 231]);
  section("SYMPTOMS", disease.symptoms, [255, 138, 0]);
  section("DIAGNOSIS", disease.diagnosis, [0, 229, 255]);
  section("TREATMENT", disease.treatment, [228, 228, 231]);
  section("PREVENTION", disease.prevention, [255, 0, 60], true);

  // Pharmacology table
  if (meds.length > 0) {
    if (y > H - 140) {
      drawFooter(pdf, pageNumber, true);
      pdf.addPage();
      y = M;
    }
    pdf.setFillColor(0, 229, 255);
    pdf.rect(M, y, W - 2 * M, 22, "F");
    pdf.setTextColor(0, 0, 0);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.text("PHARMACOLOGY · ALPHABETICAL", M + 10, y + 15);
    y += 24;

    // Header row
    pdf.setFillColor(9, 9, 11);
    pdf.rect(M, y, W - 2 * M, 16, "F");
    pdf.setTextColor(255, 255, 255);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7.5);
    const colGen = M + 6;
    const colDose = M + 170;
    const colRole = M + 290;
    pdf.text("GENERIC (BRAND)", colGen, y + 11);
    pdf.text("DOSE", colDose, y + 11);
    pdf.text("ROLE", colRole, y + 11);
    y += 18;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8.5);
    pdf.setTextColor(9, 9, 11);

    meds.forEach((m, i) => {
      // Estimate row height by wrapping role
      const roleLines = pdf.splitTextToSize(m.role || "", W - M - colRole - 6);
      const doseLines = pdf.splitTextToSize(m.dose || "", colRole - colDose - 6);
      const rowH = Math.max(roleLines.length, doseLines.length, 2) * 10 + 6;

      if (y + rowH > H - 70) {
        drawFooter(pdf, pageNumber, true);
        pdf.addPage();
        y = M;
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(9);
        pdf.setFillColor(0, 229, 255);
        pdf.rect(M, y, W - 2 * M, 22, "F");
        pdf.setTextColor(0, 0, 0);
        pdf.text(`PHARMACOLOGY · ${disease.name.toUpperCase()} (cont.)`, M + 10, y + 15);
        y += 28;
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8.5);
        pdf.setTextColor(9, 9, 11);
      }

      // Zebra background
      if (i % 2 === 0) {
        pdf.setFillColor(250, 250, 250);
        pdf.rect(M, y - 4, W - 2 * M, rowH, "F");
      }

      pdf.setFont("helvetica", "bold");
      pdf.text(m.name, colGen, y + 6);
      if (m.brand && m.brand !== "—") {
        pdf.setFont("helvetica", "italic");
        pdf.setFontSize(7.5);
        pdf.setTextColor(110, 110, 110);
        pdf.text(m.brand, colGen, y + 15);
        pdf.setFontSize(8.5);
        pdf.setTextColor(9, 9, 11);
      }

      pdf.setFont("helvetica", "normal");
      pdf.text(doseLines, colDose, y + 6);
      pdf.text(roleLines, colRole, y + 6);

      y += rowH;
    });

    // Safety advisory
    pdf.setFillColor(255, 215, 0);
    pdf.rect(M, y + 4, W - 2 * M, 18, "F");
    pdf.setTextColor(9, 9, 11);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    pdf.text("⚠ Doses are reference only — confirm with current prescribing information & treating clinician.", M + 8, y + 16);
    y += 26;
  }

  // Related Elements + Industries (compact strip)
  if ((disease.elements && disease.elements.length) || (disease.industries && disease.industries.length)) {
    if (y > H - 90) {
      drawFooter(pdf, pageNumber, true);
      pdf.addPage();
      y = M;
    }
    pdf.setFillColor(244, 244, 245);
    pdf.rect(M, y, W - 2 * M, 22, "F");
    pdf.setTextColor(9, 9, 11);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.text("RELATED ELEMENTS & INDUSTRIES", M + 10, y + 15);
    y += 28;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    if (disease.elements?.length) {
      pdf.text(`Elements: ${disease.elements.join(", ")}`, M + 5, y);
      y += 12;
    }
    if (disease.industries?.length) {
      const ind = `Industries: ${disease.industries.join(", ")}`;
      const lines = pdf.splitTextToSize(ind, W - 2 * M - 10);
      pdf.text(lines, M + 5, y);
      y += lines.length * 12;
    }
  }

  drawFooter(pdf, pageNumber, false);
}

function drawFooter(pdf, pageNumber) {
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const M = 48;
  pdf.setDrawColor(9, 9, 11);
  pdf.setLineWidth(1);
  pdf.line(M, H - 50, W - M, H - 50);
  pdf.setTextColor(100, 100, 100);
  pdf.setFontSize(8);
  pdf.setFont("helvetica", "normal");
  pdf.text(`${COMPANY.name} · ${COMPANY.brand} · ${COMPANY.web} · For training & education only`, M, H - 36);
  pdf.text(COMPANY.address, M, H - 24);
  if (pageNumber) pdf.text(`Page ${pageNumber}`, W - M, H - 36, { align: "right" });
}

/** Single disease fact sheet PDF. */
export function buildDiseaseSheet(disease) {
  const pdf = new jsPDF({ orientation: "p", unit: "pt", format: "a4" });
  drawDiseasePage(pdf, disease, null);
  pdf.save(`anagahealth-chem-${disease.id}.pdf`);
}

/** First-Aid Kit PDF — dedup'd union of all meds, sorted alphabetically. */
export function buildFirstAidKitPdf(kit) {
  const pdf = new jsPDF({ orientation: "p", unit: "pt", format: "a4" });
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const M = 48;

  // Header
  pdf.setFillColor(9, 9, 11);
  pdf.rect(0, 0, W, 110, "F");
  pdf.setFillColor(255, 215, 0);
  pdf.rect(0, 0, 18, H, "F");

  pdf.setTextColor(255, 215, 0);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(28);
  pdf.text("INDUSTRIAL FIRST-AID KIT", M, 54);
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.text(`${kit.length} unique medications · ${COMPANY.brand} formulary`, M, 72);
  pdf.setFontSize(9);
  pdf.setTextColor(180, 180, 180);
  pdf.text(`Generated ${new Date().toLocaleDateString()}  ·  ${COMPANY.web}`, M, 88);

  let y = 130;
  pdf.setFillColor(9, 9, 11);
  pdf.rect(M, y, W - 2 * M, 16, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(7.5);
  const colGen = M + 6;
  const colDose = M + 170;
  const colInd = M + 290;
  pdf.text("GENERIC (BRAND)", colGen, y + 11);
  pdf.text("DOSE", colDose, y + 11);
  pdf.text("USED FOR (DISEASES)", colInd, y + 11);
  y += 18;

  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(9, 9, 11);

  kit.forEach((item, i) => {
    const doseLines = pdf.splitTextToSize(item.doses.join(" · "), colInd - colDose - 6);
    const indLines  = pdf.splitTextToSize(item.diseases.map(d => d.name).join(", "), W - M - colInd - 6);
    const rowH = Math.max(doseLines.length, indLines.length, 2) * 10 + 6;

    if (y + rowH > H - 70) {
      drawFooter(pdf, null);
      pdf.addPage();
      y = M;
      pdf.setFillColor(9, 9, 11);
      pdf.rect(M, y, W - 2 * M, 16, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(7.5);
      pdf.text("GENERIC (BRAND)", colGen, y + 11);
      pdf.text("DOSE", colDose, y + 11);
      pdf.text("USED FOR (DISEASES)", colInd, y + 11);
      y += 18;
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(9, 9, 11);
    }

    if (i % 2 === 0) {
      pdf.setFillColor(250, 250, 250);
      pdf.rect(M, y - 4, W - 2 * M, rowH, "F");
    }

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8.5);
    pdf.text(item.name, colGen, y + 6);
    if (item.brand && item.brand !== "—") {
      pdf.setFont("helvetica", "italic");
      pdf.setFontSize(7.5);
      pdf.setTextColor(110, 110, 110);
      pdf.text(item.brand, colGen, y + 15);
      pdf.setFontSize(8.5);
      pdf.setTextColor(9, 9, 11);
    }
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.text(doseLines, colDose, y + 6);
    pdf.text(indLines, colInd, y + 6);
    y += rowH;
  });

  drawFooter(pdf, null);
  pdf.save("anagahealth-chem-first-aid-kit.pdf");
}
